// Réécriture des URLs de menu saisies en absolu vers le domaine custom de l'org.
//
// CONTEXTE : dans le backoffice, les items de menu de type LINK sont saisis à la
// main. Les admins collent souvent l'URL absolue "https://kehila.io/<prefix>/..."
// au lieu d'un chemin relatif. Quand l'org a un domaine custom, ces liens
// pointent alors vers kehila.io → double-hop (le RouterService redirige ensuite)
// et les liens "fuient" hors du domaine custom.
//
// RÈGLE (volontairement simple) :
//   1. URL relative ("/don")        → BASE_WEB_URL + url
//   2. URL absolue qui commence par WEB_URL + "/" + prefix  (= cette org sur le
//      SaaS) → on remplace ce préfixe par BASE_WEB_URL.
//   3. SLUG NU sans "/" initial ("shabbat-meals-rsvp") → BASE_WEB_URL + "/" + url.
//      Cas réel : un item de menu de type FORM/CAGNOTTE hérite du slug de l'entité
//      (form.url), qui est stocké SANS "/" de tête (ex. "shabbat-meals-rsvp").
//      Sans ce traitement, <Link href="shabbat-meals-rsvp"> est résolu par le
//      navigateur RELATIVEMENT à l'URL courante → le préfixe d'org est écrasé
//      ("kehila.io/<prefix>" → "kehila.io/shabbat-meals-rsvp").
//   4. tout le reste (autre org "kehila.io/autreorg", lien externe, mailto:, tel:,
//      ancre "#x", schéma "xxx:"…) → inchangé.

function stripTrailingSlashes(s) {
    return (s || "").replace(/\/+$/, "");
}

// Construit l'URL finale d'un item de menu pour les thèmes.
export function buildMenuUrl(rawUrl, org) {
    const url = (rawUrl || "").trim();
    if (!url || url === "#") return "#";

    const base = stripTrailingSlashes(org?.BASE_WEB_URL || ""); // ex. https://habadcorse.fr

    // URL protocol-relative ("//host/...") : c'est une URL absolue déguisée, pas
    // un chemin. On ne la préfixe jamais — laissée telle quelle.
    if (url.startsWith("//")) return url;

    // 1. Chemin relatif ("/don") : on préfixe par le domaine de l'org.
    if (url.startsWith("/")) {
        return base ? base + url : url;
    }

    // 2. Absolu http(s) visant cette org sur le SaaS → réécrire vers le domaine.
    if (/^https?:\/\//i.test(url)) {
        return rewriteAbsoluteToDomain(url, org);
    }

    // 3bis. Schéma URI (mailto:, tel:, sms:, whatsapp:, javascript:, data:, ftp:…)
    // ou ancre/query nue ("#x", "?x") → JAMAIS un slug, laissé tel quel.
    // Détection d'un schéma au sens RFC 3986 : ^scheme: où scheme = lettre suivie
    // de lettres/chiffres/+/-/. (http(s):// est déjà traité au-dessus, donc ici on
    // ne capte que les AUTRES schémas). On exclut aussi les ancres et query nues
    // qui se résolvent volontairement contre l'URL courante.
    if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith("#") || url.startsWith("?")) {
        return url;
    }

    // 3. SLUG NU ("shabbat-meals-rsvp", "don/123") : pas de "/" de tête, pas de
    // schéma → chemin relatif à l'org. On préfixe par BASE_WEB_URL + "/" pour ne
    // pas dépendre de la résolution relative du navigateur (qui écraserait le
    // préfixe d'org). Sans BASE_WEB_URL (dégradé), on garantit au moins un "/" de
    // tête pour que le lien reste ancré à la racine.
    return base ? base + "/" + url : "/" + url;
}

// Réécrit "https://kehila.io/{prefix}/reste" → "BASE_WEB_URL/reste".
// Toute autre URL absolue (autre prefix, autre domaine) est renvoyée intacte.
export function rewriteAbsoluteToDomain(rawUrl, org) {
    const url = (rawUrl || "").trim();
    if (!/^https?:\/\//i.test(url)) return url;

    const base = stripTrailingSlashes(org?.BASE_WEB_URL || ""); // https://habadcorse.fr
    const webUrl = stripTrailingSlashes(org?.WEB_URL || "");    // https://kehila.io
    const prefix = org?.orgPrefix || org?.prefix || "";
    if (!base || !webUrl || !prefix) return url;

    // Helper : strippe `marker` en tête de `url` (suivi d'une fin de segment), et
    // renvoie le reste ("" | "/x" | "?x" | "#x"), ou null si pas de match.
    const stripPrefix = (marker) => {
        const re = new RegExp(
            "^" + marker.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?=[/?#]|$)",
            "i"
        );
        if (!re.test(url)) return null;
        let rest = url.replace(re, "");
        if (rest === "/") rest = "";
        return rest;
    };

    // 1. PRIORITÉ à `base` complet (= WEB_URL/prefix + ÉVENTUEL préfixe de langue
    //    courant, ajouté par RouterService). Indispensable quand l'URL d'entrée est
    //    DÉJÀ une URL finale absolue de cette org sur le SaaS, langue comprise (ex.
    //    item d'article dynamique "localhost/bh_deauville/en/jewish-educations/..").
    //    Sans ça, on stripperait seulement WEB_URL/prefix (sans langue) puis on
    //    recollerait `base` (AVEC langue) → langue DOUBLÉE "/en/en/..".
    //    (No-op quand base === webUrl/prefix, i.e. langue par défaut sans préfixe.)
    if (base !== webUrl + "/" + prefix) {
        const rest = stripPrefix(base);
        if (rest !== null) return base + rest;
    }

    // 2. Sinon : préfixe attendu "https://kehila.io/habadcorse" (SANS langue) →
    //    réécrit vers le domaine. Cas d'une URL absolue collée à la main par
    //    l'admin pointant vers kehila.io alors que l'org a un domaine custom.
    const rest = stripPrefix(webUrl + "/" + prefix);
    if (rest === null) return url;
    return base + rest;
}

// ---------------------------------------------------------------------------
// isExternalUrl(url, org) — un lien doit-il s'ouvrir dans un NOUVEL ONGLET ?
//
// "externe" = ne pointe PAS vers le site de CETTE org. Sert à décider
// target="_blank" sur les liens de menu/boutons des thèmes.
//
// POURQUOI un helper dédié (et pas `!url.startsWith(org.BASE_WEB_URL)`) :
// l'ancien check supposait qu'un lien interne commence TOUJOURS par
// BASE_WEB_URL exact. FAUX dès que BASE_WEB_URL porte un suffixe que le lien
// interne n'a pas — typiquement le préfixe de langue ("…/chabadclujnapoca/he")
// ajouté par RouterService, ou "/__embed". Un lien légitime
// "…/chabadclujnapoca/mikvah" était alors vu comme externe → _blank à tort.
//
// RÈGLE :
//   - relatif ("/x"), slug nu ("x"), ancre/query ("#x"/"?x"), autre schéma
//     (mailto:/tel:/…), protocol-relative ("//host") → JAMAIS externe (false) :
//     soit interne au site, soit géré ailleurs (jamais un _blank de menu).
//   - absolu http(s) → INTERNE s'il vise une "racine interne" de l'org
//     (domaine custom, ou WEB_URL/prefix), en IGNORANT le suffixe de langue.
//     Toute autre URL absolue (autre org du SaaS, site tiers) → externe.
function stripUrlSuffix(u) {
    // Retire query/hash + slash final pour comparer des racines proprement.
    return stripTrailingSlashes((u || "").split("#")[0].split("?")[0]);
}

// Origine ("https://host[:port]") d'une URL absolue, ou null.
function urlOrigin(u) {
    const m = (u || "").match(/^(https?:\/\/[^/]+)/i);
    return m ? m[1] : null;
}

// Racines qui identifient le site de l'org, indépendamment de la langue.
// Une URL absolue est "interne" si elle commence par l'une de ces racines.
// Deux familles de racines :
//   A. DOMAINE CUSTOM → l'origine seule ("https://habadcorse.fr") suffit : tout
//      le domaine appartient à l'org. Détecté quand l'origine de BASE_WEB_URL
//      DIFFÈRE du mainHost (WEB_URL) — fiable même si org.domain n'est pas
//      renseigné (cas dégradés). On ne se fie PAS au seul champ org.domain.
//   B. MAINHOST (kehila.io) → l'origine NE suffit PAS (sinon une AUTRE org sur
//      le même host serait vue interne) : la racine est "WEB_URL/prefix", et le
//      préfixe de langue éventuel vient APRÈS (absorbé par la frontière de
//      segment au moment du test).
function internalRoots(org) {
    const roots = [];
    const webUrl = stripTrailingSlashes(org?.WEB_URL || "");
    const prefix = org?.orgPrefix || org?.prefix || "";
    const base = stripUrlSuffix(org?.BASE_WEB_URL || "");

    const baseOrigin = urlOrigin(base);
    const mainOrigin = urlOrigin(webUrl);

    // A. Domaine custom : origine de BASE_WEB_URL ≠ mainHost → tout le domaine.
    if (baseOrigin && baseOrigin !== mainOrigin) {
        roots.push(baseOrigin);
    }

    // B. Sur le SaaS : un lien "WEB_URL/prefix[/lang]" pointe vers cette org
    //    (vrai aussi pour une org à domaine custom : rewriteAbsoluteToDomain le
    //    ramènerait au domaine). Frontière de segment gérée au test.
    if (webUrl && prefix) roots.push(webUrl + "/" + prefix);

    return roots;
}

export function isExternalUrl(url, org) {
    const raw = (url || "").trim();
    if (!raw) return false;

    // Non-absolu (relatif, slug, ancre, query, schéma non-http, protocol-relative)
    // → jamais un lien externe de menu.
    if (!/^https?:\/\//i.test(raw)) return false;

    const roots = internalRoots(org);

    // Interne si l'URL commence par une racine de l'org, suivie d'une fin de
    // segment (/, ?, #, ou fin) — évite que "…/prefix-bis" matche "…/prefix".
    for (const root of roots) {
        const re = new RegExp(
            "^" + root.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?=[/?#]|$)",
            "i"
        );
        if (re.test(raw)) return false; // interne
    }
    return true; // absolu + aucune racine interne → externe
}

// ---------------------------------------------------------------------------
// buildLinkHref(url, org) — href propre pour un <Link> Next.
//
// PROBLÈME : passer une URL ABSOLUE same-origin à <Link href> fait que Next
// la route contre le catch-all `pages/[...router].js` et réinjecte les segments
// du chemin en query → ".../fr/mikvah?router=chabadclujnapoca&router=fr&router=
// mikvah". Le path reste juste (la page s'affiche) mais l'URL est polluée.
//
// SOLUTION : si l'URL vise le HOST COURANT, on la réduit à son chemin
// (path+query+hash) → <Link> navigue proprement, sans ?router=. Si elle vise un
// AUTRE host (domaine custom multi-devise, lien cross-domaine), on garde
// l'absolu (vraie navigation cross-origin, que <Link> laisse passer tel quel).
//
// "host courant" : au client = window.location.origin (source de vérité) ; en
// SSR (pas de window) = origine de BASE_WEB_URL (= le host servi dans le cas
// normal). Non-absolu (relatif/slug/ancre/schéma) → renvoyé tel quel.
export function buildLinkHref(url, org) {
    const raw = (url || "").trim();
    if (!raw || !/^https?:\/\//i.test(raw)) return url;

    const urlOri = urlOrigin(raw);
    if (!urlOri) return url;

    // Origine du host courant : window au client, sinon origine de BASE_WEB_URL.
    let currentOrigin = null;
    if (typeof window !== "undefined" && window.location) {
        currentOrigin = window.location.origin;
    } else {
        currentOrigin = urlOrigin(stripTrailingSlashes(org?.BASE_WEB_URL || ""));
    }
    if (!currentOrigin) return url;

    // Même origine → on relativise (retire le schéma+host, garde "/path?query#hash").
    if (urlOri.toLowerCase() === currentOrigin.toLowerCase()) {
        const rest = raw.slice(urlOri.length);
        return rest || "/";
    }

    // Origine différente (cross-domaine) → on garde l'absolu.
    return url;
}

// ---------------------------------------------------------------------------
// buildCanonicalUrl(org, ctxUrl) — URL canonique absolue pour <link rel="canonical">
// et og:url dans les <Meta> de thème.
//
// PROBLÈME (bug corrigé) : les Meta construisaient `BASE_WEB_URL + context.url`.
// Or `context.url` (posé par RouterService = `req.headers.host + req.url`) contient
// DÉJÀ l'host + le préfixe org/langue (ex. "localhost:4000/bh_tourism/fr/travel/x"),
// et sans schéma. Le concaténer à BASE_WEB_URL (qui a déjà host+préfixe) doublait
// tout → "…/bh_tourism/frlocalhost:4000/bh_tourism/fr/travel/x". La query traînait
// aussi (canonical dupliqués côté SEO).
//
// RÈGLE : l'URL canonique = SCHÉMA de BASE_WEB_URL (http/https, défaut https) +
// `context.url` (qui porte déjà host + path complet), query/hash et slash final
// retirés. Indépendant du préfixe (SaaS org/langue OU domaine custom) puisque
// context.url l'inclut déjà. Renvoie null si l'info manque.
export function buildCanonicalUrl(org, ctxUrl) {
    const raw = (ctxUrl || "").trim();
    if (!raw) return null;

    // Retire query, hash et slash(es) final(aux) pour un canonical propre.
    const clean = (u) => stripTrailingSlashes(u.split("#")[0].split("?")[0]);

    // Par prudence : si context.url portait déjà un schéma, on le respecte tel quel.
    if (/^https?:\/\//i.test(raw)) {
        const c = clean(raw);
        return c || null;
    }

    // Schéma repris de BASE_WEB_URL (le host servi), défaut https.
    const base = stripTrailingSlashes(org?.BASE_WEB_URL || "");
    const scheme = (base.match(/^(https?):\/\//i)?.[1] || "https").toLowerCase();

    const hostAndPath = clean(raw); // "host[:port]/path"
    if (!hostAndPath) return null;

    return `${scheme}://${hostAndPath}`;
}
